/**
 * The app bar from `sm` up (phones get the bottom bar and its More drawer
 * instead): the wordmark, then on the right Join DeepThink for a free-plan
 * student and the account pill (`AccountMenu`), whose panel holds the role,
 * the email, the sample-student switch and Log out.
 *
 * Nothing else rides in the bar, so from a 600px tablet beside the nav rail
 * to a desktop nothing wraps or runs off the edge; only the account's name
 * may shorten. The bar is 64px with its content centred, a hairline under it.
 * Design: docs/designs/app-header-2026-10-13/design.md (DESIGN.md §8).
 *
 * Update when the bar gains or loses a control, or the upsell rules change.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Typography,
  type TypographyProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { accountDisplayName, formatRoleLabel } from '@/auth/accountIdentity';
import { accountStateOf } from '@/auth/accountState';
import AccountMenu from '@/components/common/AccountMenu';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { NAV_RAIL_WIDTH, navWidthTransitionSx } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useEntitlement } from '@/hooks/useEntitlement';
import { shouldShowAuthenticatedUpsell } from '@/utils/upsellEligibility';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  // A hairline inside the bar's bottom edge, so the bar stays the 64px the
  // page's spacer and the sticky offsets under it assume.
  boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
}));

// The explicit prop type restores Typography's polymorphic `component`, which
// `styled()` otherwise narrows away: the wordmark is pinned to <p> so
// `variant="h6"` stays a type-scale choice and never emits a stray heading.
const LogoText = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '1.5rem',
}));

/**
 * The bar's buttons: 40px pills that never shrink or wrap, each with a 48px
 * touch target. The theme gives every Button a 48px floor (44px from md) in
 * a nested media query, which outranks a flat `minHeight`, so both restate it.
 */
const barButtonSx = {
  position: 'relative',
  flexShrink: 0,
  height: 40,
  minHeight: 40,
  '@media (min-width:900px)': { minHeight: 40 },
  px: 2,
  borderRadius: 'var(--radius-pill)',
  fontSize: 14,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  '&::after': { content: '""', position: 'absolute', inset: '-4px 0' },
} as const;

/**
 * Join DeepThink in orange-dark with a white label (5.2:1, DESIGN.md §10's
 * smaller-orange rule): plainly the brand action, without matching the
 * page's own orange Start beside it.
 */
const joinSx = {
  ...barButtonSx,
  fontWeight: 800,
  letterSpacing: '-0.01em',
  color: 'var(--paper)',
  backgroundColor: 'var(--orange-dark)',
  transition:
    'transform 80ms var(--ease-default), background-color 150ms var(--ease-default)',
  '&:hover': {
    backgroundColor: 'var(--orange-dark)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(1px)' },
  '&:focus-visible': { outline: '2px solid var(--cobalt)', outlineOffset: 2 },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'background-color 150ms var(--ease-default)',
    '&:hover': { transform: 'none' },
    '&:active': { transform: 'none' },
  },
} as const;

interface HeaderProps {
  /**
   * Desktop nav is collapsed to the icon rail: offset the wordmark so it
   * sits beside the rail instead of hidden under the full-width drawer.
   */
  navRailActive?: boolean;
}

const Header: React.FC<HeaderProps> = ({ navRailActive = false }) => {
  const {
    user,
    isAuthenticated,
    isAuthLoading,
    userRole,
    isRoleLoaded,
    userStatus,
    authError,
    demoView,
    loginWithPath,
    logoutUser,
  } = useAuth();
  const location = useLocation();
  const {
    isFree,
    loading: entitlementLoading,
    error: entitlementError,
  } = useEntitlement();
  const [upsellOpen, setUpsellOpen] = useState(false);
  // The role shows only once the server has confirmed it and the account is
  // active; until then the provider's starting default (a student) would
  // label a teacher "Student". An account awaiting activation says so.
  const accountState = accountStateOf({
    isRoleLoaded,
    userStatus,
    authError: authError ?? null,
  });
  // A failed entitlement read falls back to the free plan, and a demo
  // teacher's sample student is not theirs to upgrade: neither offers Join.
  const showJoinButton =
    (accountState === 'active' || accountState === 'inactive') &&
    !entitlementLoading &&
    !entitlementError &&
    !demoView?.active &&
    shouldShowAuthenticatedUpsell(userRole, isFree);

  let accountControls: React.ReactNode;
  if (isAuthLoading) {
    // Auth0 is still restoring the session: hold the pill's place rather
    // than flash "Log in" at someone who is logged in.
    accountControls = (
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 128,
          height: 40,
          borderRadius: 'var(--radius-pill)',
          backgroundColor: 'var(--bg-alt)',
        }}
      />
    );
  } else if (!isAuthenticated) {
    accountControls = (
      <Button
        variant="contained"
        color="primary"
        onClick={() =>
          loginWithPath(location.pathname + location.search + location.hash)
        }
        sx={{ ...barButtonSx, fontWeight: 700 }}
      >
        Log in
      </Button>
    );
  } else {
    accountControls = (
      <>
        {showJoinButton && (
          <Button
            variant="contained"
            onClick={() => setUpsellOpen(true)}
            sx={joinSx}
          >
            Join DeepThink
          </Button>
        )}
        <AccountMenu
          name={accountDisplayName(user)}
          email={user?.email ?? null}
          roleLabel={accountState === 'active' ? formatRoleLabel(userRole) : ''}
          accountState={accountState}
          onLogOut={logoutUser}
        />
      </>
    );
  }

  return (
    <>
      <StyledAppBar
        position="fixed"
        className="safe-header"
        sx={{ display: { xs: 'none', sm: 'block' } }}
      >
        <Toolbar
          disableGutters
          sx={{ gap: 3, minHeight: { sm: 64 }, px: { sm: 2, md: 3 } }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              ml: navRailActive ? `${NAV_RAIL_WIDTH}px` : 0,
              ...navWidthTransitionSx('margin-left'),
            }}
          >
            <LogoText variant="h6" component="p" noWrap>
              DeepThink
            </LogoText>
          </Box>
          <Box
            sx={{
              ml: 'auto',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            {accountControls}
          </Box>
        </Toolbar>
      </StyledAppBar>
      <ProUpsellDialog
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        sourceLocation="header_join"
      />
    </>
  );
};

export default Header;
